import React from 'react';
import { AlertTriangle, CirclePlus, PencilLine, Plus, Trash2 } from 'lucide-react';
import ProposalDiff from './ProposalDiff';
import { SmartDiffInput, SmartDiffList } from './SmartDiffFields';

const ACTIONS = [
  {
    value: 'edit',
    label: 'Modifier',
    detail: 'Ajuster un role existant',
    icon: PencilLine,
    activeClass: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  },
  {
    value: 'create',
    label: 'Creer',
    detail: 'Ajouter un nouveau role',
    icon: CirclePlus,
    activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  },
  {
    value: 'delete',
    label: 'Supprimer',
    detail: 'Retirer un role',
    icon: Trash2,
    activeClass: 'border-red-300 bg-red-50 text-red-700',
  },
];

const ACTION_LABELS = {
  create: 'Creation',
  edit: 'Modification',
  delete: 'Suppression',
};

const OperationCard = ({ op, idx, circles, disabled, onUpdate, onRemove }) => {
  const rolesInCircle = circles.filter((c) => String(c.parent_id) === String(op.circle_id));
  const parentRole = circles.find((c) => String(c.id) === String(op.circle_id));
  const rolesInScope = parentRole ? [parentRole, ...rolesInCircle] : rolesInCircle;
  const roleSnapshot = (roleId) => {
    const role = circles.find((c) => String(c.id) === String(roleId));
    if (!role) return {};
    return {
      name: role.name || '',
      purpose: role.purpose || '',
      domains: role.domains || [],
      accountabilities: role.accountabilities || [],
      parent_id: role.parent_id || null,
    };
  };

  const [originalSnapshot, setOriginalSnapshot] = React.useState(
    op.original_data || (op.action !== 'create' && op.target_circle_id ? roleSnapshot(op.target_circle_id) : {})
  );

  React.useEffect(() => {
    if (op.original_data) {
      setOriginalSnapshot(op.original_data);
      return;
    }
    if (op.action !== 'create' && op.target_circle_id) {
      const snapshot = roleSnapshot(op.target_circle_id);
      setOriginalSnapshot(snapshot);
      const hasData = op.data && Object.keys(op.data).length > 0;
      onUpdate(idx, { ...op, original_data: snapshot, data: hasData ? op.data : snapshot });
    } else if (op.action === 'create' && !op.original_data) {
      setOriginalSnapshot({});
      onUpdate(idx, { ...op, original_data: {} });
    }
  }, [op.action, op.target_circle_id, op.original_data, op.data, circles]);

  const currentData = op.data || {};
  const effectiveData = {
    name: currentData.name !== undefined ? currentData.name : (originalSnapshot?.name || ''),
    purpose: currentData.purpose !== undefined ? currentData.purpose : (originalSnapshot?.purpose || ''),
    domains: currentData.domains !== undefined ? currentData.domains : (originalSnapshot?.domains || []),
    accountabilities: currentData.accountabilities !== undefined ? currentData.accountabilities : (originalSnapshot?.accountabilities || []),
    parent_id: currentData.parent_id !== undefined ? currentData.parent_id : (originalSnapshot?.parent_id ?? null),
  };
  const currentParentId = currentData.parent_id ?? op.circle_id ?? null;

  const handleActionChange = (val) => {
    if (val === 'create') {
      setOriginalSnapshot({});
      onUpdate(idx, {
        ...op,
        action: val,
        target_circle_id: null,
        original_data: {},
        data: { name: '', purpose: '', domains: [], accountabilities: [], parent_id: op.circle_id || null },
      });
      return;
    }

    if (val === 'delete') {
      const nextTargetId = op.target_circle_id || op.circle_id || null;
      const snapshot = nextTargetId ? roleSnapshot(nextTargetId) : originalSnapshot || {};
      setOriginalSnapshot(snapshot || {});
      onUpdate(idx, { ...op, action: val, target_circle_id: nextTargetId, original_data: snapshot || {}, data: {} });
      return;
    }

    const nextTargetId = op.target_circle_id || op.circle_id || null;
    const snapshot = nextTargetId ? roleSnapshot(nextTargetId) : originalSnapshot || {};
    const hasData = Object.keys(currentData || {}).length > 0;
    const shouldResetToSnapshot = !hasData || op.action === 'create';
    setOriginalSnapshot(snapshot || {});
    onUpdate(idx, {
      ...op,
      action: val,
      target_circle_id: nextTargetId,
      original_data: snapshot || {},
      data: shouldResetToSnapshot ? (snapshot || {}) : currentData,
    });
  };

  const handleTargetChange = (val) => {
    const snapshot = val ? roleSnapshot(val) : {};
    setOriginalSnapshot(snapshot);
    const nextData = val ? snapshot : { ...(currentData || {}), parent_id: op.circle_id || null };
    onUpdate(idx, {
      ...op,
      target_circle_id: val,
      original_data: snapshot,
      data: nextData,
    });
  };

  const updateDataField = (key, val) => {
    onUpdate(idx, { ...op, data: { ...(currentData || {}), [key]: val } });
  };

  const updateListField = (key, list) => {
    onUpdate(idx, { ...op, data: { ...(currentData || {}), [key]: list } });
  };

  const currentAction = op.action || 'edit';
  const selectedTarget = currentAction === 'create'
    ? circles.find((c) => String(c.id) === String(currentParentId))
    : circles.find((c) => String(c.id) === String(op.target_circle_id));

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase text-slate-500 font-semibold">Action {idx + 1}</p>
          <p className="text-sm font-semibold text-slate-900">
            {ACTION_LABELS[currentAction] || 'Modification'}
            {selectedTarget?.name ? ` - ${selectedTarget.name}` : ''}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 shrink-0"
          >
            <Trash2 size={14} /> Supprimer
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {ACTIONS.map((actionOption) => {
          const Icon = actionOption.icon;
          const active = currentAction === actionOption.value;
          return (
            <button
              key={actionOption.value}
              type="button"
              onClick={() => handleActionChange(actionOption.value)}
              disabled={disabled}
              className={`min-h-[64px] rounded-lg border px-3 py-2 text-left transition ${
                active
                  ? actionOption.activeClass
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Icon size={15} />
                {actionOption.label}
              </span>
              <span className="mt-1 block text-[11px] text-slate-500">{actionOption.detail}</span>
            </button>
          );
        })}
        <select
          value={op.action || 'edit'}
          onChange={(e) => handleActionChange(e.target.value)}
          className="sr-only"
          disabled={disabled}
        >
          <option value="create">Créer un role</option>
          <option value="edit">Modifier un role</option>
          <option value="delete">Supprimer un role</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500 font-semibold uppercase">
            {op.action === 'create' ? 'Creer sous' : op.action === 'delete' ? 'Element a supprimer' : 'Element a modifier'}
          </p>
        {op.action === 'create' ? (
          <select
            value={currentParentId || ''}
            onChange={(e) => updateDataField('parent_id', e.target.value ? Number(e.target.value) : null)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            disabled={disabled || !op.circle_id}
          >
            <option value="">Choisir un parent</option>
            {rolesInScope.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{String(c.id) === String(op.circle_id) ? ' (cercle)' : ''}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={op.target_circle_id || ''}
            onChange={(e) => handleTargetChange(e.target.value ? Number(e.target.value) : null)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            disabled={disabled || !op.circle_id}
          >
            <option value="">Choisir un role/cercle a {op.action === 'delete' ? 'supprimer' : 'modifier'}</option>
            {rolesInScope.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        </div>
      </div>

      {op.action === 'delete' && (
        <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Cette action supprimera {selectedTarget?.name || 'le role selectionne'} si la tension est acceptee.
          </span>
        </div>
      )}

      {op.action !== 'delete' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SmartDiffInput
              label="Nom du role"
              originalValue={originalSnapshot?.name || ''}
              currentValue={effectiveData.name}
              onChange={(v) => updateDataField('name', v)}
              placeholder="Nom du role"
              disabled={disabled}
            />
            <SmartDiffInput
              label="Raison d'etre"
              originalValue={originalSnapshot?.purpose || ''}
              currentValue={effectiveData.purpose}
              onChange={(v) => updateDataField('purpose', v)}
              placeholder="Raison d'etre"
              multiline
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SmartDiffList
              label="Domaines"
              originalList={originalSnapshot?.domains || []}
              currentList={effectiveData.domains}
              onChange={(list) => updateListField('domains', list)}
              disabled={disabled}
            />
            <SmartDiffList
              label="Redevabilites"
              originalList={originalSnapshot?.accountabilities || []}
              currentList={effectiveData.accountabilities}
              onChange={(list) => updateListField('accountabilities', list)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const TensionEditor = ({
  tensionDraft,
  circles,
  user,
  tensionVotes,
  tensionVotesLoading,
  tensionsLoading,
  isFieldChanged,
  onUpdateField,
  onUpdateProposedField,
  onUpdateList,
  onChangeAction,
  onDelete,
  onPropose,
  onWithdraw,
  onCancel,
  onSave,
  mode = 'tension',
  readOnly = false,
  expressedRoleOptions,
  defaultCircleId,
}) => {
  const [localOps, setLocalOps] = React.useState(tensionDraft?.operations || []);
  const resolveVoteTimeoutDays = React.useCallback(
    () => {
      if (!tensionDraft) return 3;
      const proposedParent = tensionDraft.proposed_data?.parent_id;
      let referenceCircle = null;
      if (proposedParent) {
        referenceCircle = (circles || []).find((c) => String(c.id) === String(proposedParent));
      } else {
        const target = (circles || []).find((c) => String(c.id) === String(tensionDraft.circle_id));
        if (target?.parent_id) {
          referenceCircle = (circles || []).find((c) => String(c.id) === String(target.parent_id)) || target;
        } else {
          referenceCircle = target || null;
        }
      }
      const raw = Number(referenceCircle?.vote_timeout_days || 0);
      if (Number.isNaN(raw) || raw < 1) return 3;
      return raw;
    },
    [tensionDraft, circles]
  );
  const effectiveUserId = user?.employee_id ?? user?.id;
  const assignedRoles = React.useMemo(() => {
    if (!effectiveUserId) return [];
    const userId = String(effectiveUserId);
    return (circles || []).filter((circle) => {
      if (!circle) return false;
      if (circle.owner_id !== undefined && String(circle.owner_id) === userId) return true;
      if (Array.isArray(circle.owner_ids)) {
        return circle.owner_ids.some((id) => String(id) === userId);
      }
      return false;
    });
  }, [circles, effectiveUserId]);
  const circleOptions = React.useMemo(() => {
    const list = circles || [];
    if (list.length === 0) return [];
    const childrenByParent = {};
    list.forEach((circle) => {
      if (!circle?.parent_id) return;
      const key = String(circle.parent_id);
      childrenByParent[key] = (childrenByParent[key] || 0) + 1;
    });
    return list.filter((circle) => {
      if (!circle) return false;
      const hasParent = circle.parent_id !== null && circle.parent_id !== undefined && circle.parent_id !== '';
      const hasChildren = Boolean(childrenByParent[String(circle.id)]);
      return !hasParent || hasChildren;
    });
  }, [circles]);

  const snapshotFromCircle = React.useCallback((circle) => ({
    name: circle?.name || '',
    purpose: circle?.purpose || '',
    domains: circle?.domains || [],
    accountabilities: circle?.accountabilities || [],
    parent_id: circle?.parent_id ?? null,
  }), []);

  const buildOperation = React.useCallback((action = 'edit', circleId = null) => {
    const targetCircle = (circles || []).find((c) => String(c.id) === String(circleId));
    const targetSnapshot = snapshotFromCircle(targetCircle);
    if (action === 'create') {
      return {
        action: 'create',
        circle_id: circleId || null,
        target_circle_id: null,
        data: { name: '', purpose: '', domains: [], accountabilities: [], parent_id: circleId || null },
        original_data: {},
      };
    }
    if (action === 'delete') {
      return {
        action: 'delete',
        circle_id: circleId || null,
        target_circle_id: circleId || null,
        data: {},
        original_data: targetSnapshot,
      };
    }
    return {
      action: 'edit',
      circle_id: circleId || null,
      target_circle_id: circleId || null,
      data: targetSnapshot,
      original_data: targetSnapshot,
    };
  }, [circles, snapshotFromCircle]);

  const updateOps = React.useCallback((ops) => {
    setLocalOps(ops);
    onUpdateField && onUpdateField('operations', ops);
  }, [onUpdateField]);

  const applyCircleContext = React.useCallback((circleId, action = 'edit') => {
    if (!circleId) return;
    const selected = (circles || []).find((c) => String(c.id) === String(circleId));
    const snapshot = snapshotFromCircle(selected);
    onUpdateField && onUpdateField('circle_id', Number(circleId));
    onUpdateField && onUpdateField('original_data', snapshot);
    onUpdateField && onUpdateField('proposed_data', snapshot);
    updateOps([buildOperation(action, Number(circleId))]);
  }, [buildOperation, circles, onUpdateField, snapshotFromCircle, updateOps]);

  React.useEffect(() => {
    setLocalOps(tensionDraft?.operations || []);
  }, [tensionDraft?.operations, tensionDraft?.id]);

  React.useEffect(() => {
    if (!tensionDraft || readOnly || tensionDraft.status === 'proposal') return;
    if (tensionDraft.circle_id) return;
    const fallbackCircleId = defaultCircleId || circleOptions[0]?.id;
    if (!fallbackCircleId) return;
    applyCircleContext(fallbackCircleId, tensionDraft.action || 'edit');
  }, [
    applyCircleContext,
    circleOptions,
    defaultCircleId,
    readOnly,
    tensionDraft,
  ]);

  const safeTension = tensionDraft || {};
  const isProposal = safeTension.status === 'proposal';
  const isAuthor = effectiveUserId && safeTension.author_id && String(effectiveUserId) === String(safeTension.author_id);
  const isLocked = isProposal || readOnly;
  const showTopActions = mode !== 'meeting';
  const showFooterActions = mode !== 'meeting' || !readOnly;
  const expressedRoles = expressedRoleOptions && expressedRoleOptions.length > 0 ? expressedRoleOptions : assignedRoles;
  const autoAcceptAt = React.useMemo(() => {
    if (!isProposal) return null;
    if (!Array.isArray(tensionVotes) || tensionVotes.length === 0) return null;
    const createdDates = tensionVotes
      .map((vote) => (vote?.created_at ? new Date(vote.created_at) : null))
      .filter((date) => date && !Number.isNaN(date.getTime()));
    if (createdDates.length === 0) return null;
    const start = createdDates.reduce((min, current) => (current < min ? current : min), createdDates[0]);
    const timeoutDays = resolveVoteTimeoutDays();
    const autoAcceptDate = new Date(start.getTime() + timeoutDays * 24 * 60 * 60 * 1000);
    if (Number.isNaN(autoAcceptDate.getTime())) return null;
    return autoAcceptDate;
  }, [isProposal, tensionVotes, resolveVoteTimeoutDays]);
  const autoAcceptLabel = autoAcceptAt
    ? autoAcceptAt.toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  if (!tensionDraft) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-sm">
        Sélectionnez ou créez une tension pour la pr?visualiser.
      </div>
    );
  }

  const addOperation = (action = 'edit') => {
    const circleId = tensionDraft.circle_id || defaultCircleId || circleOptions[0]?.id || null;
    if (!tensionDraft.circle_id && circleId) {
      const selected = (circles || []).find((c) => String(c.id) === String(circleId));
      const snapshot = snapshotFromCircle(selected);
      onUpdateField && onUpdateField('circle_id', Number(circleId));
      onUpdateField && onUpdateField('original_data', snapshot);
      onUpdateField && onUpdateField('proposed_data', snapshot);
    }
    const next = [...localOps, buildOperation(action, circleId ? Number(circleId) : null)];
    updateOps(next);
  };

  const handleUpdateOp = (idx, nextOp) => {
    updateOps(localOps.map((op, i) => (i === idx ? nextOp : op)));
  };

  const handleRemoveOp = (idx) => {
    updateOps(localOps.filter((_, i) => i !== idx));
  };

  if (isProposal) {
    const circleLabel =
      tensionDraft.original_data?.name || circles.find((c) => c.id === tensionDraft.circle_id)?.name || 'Role';
    const action = tensionDraft.action;
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-purple-700 uppercase">Proposition en cours de vote</p>
            <h3 className="text-lg font-bold text-slate-900">{tensionDraft.title || circleLabel}</h3>
            {tensionDraft.summary && <p className="text-sm text-slate-600 mt-1">{tensionDraft.summary}</p>}
            <p className="text-xs text-slate-500 mt-1">Role concerne : {circleLabel}</p>
          </div>
          <div className="flex gap-2">
            {tensionDraft.id && tensionDraft.status === 'proposal' && isAuthor && (
              <button
                onClick={() => onWithdraw(tensionDraft.id)}
                className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 flex items-center text-sm"
                title="Retirer la proposition"
              >
                Retirer
              </button>
            )}
            {tensionDraft.id && (
              <button
                onClick={() => onDelete(tensionDraft.id)}
                className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center text-sm"
              >
                <Trash2 size={14} className="mr-1" /> Supprimer
              </button>
            )}
          </div>
        </div>

        {localOps.length > 0 ? (
          <div className="space-y-2 text-sm text-slate-700">
            <p className="text-xs uppercase text-slate-500 font-semibold">Operations</p>
            <ul className="space-y-1">
              {localOps.map((op, idx) => {
                const roleName =
                  op.target_circle_id === null
                    ? 'Nouveau role'
                    : circles.find((c) => c.id === op.target_circle_id)?.name || `Role #${op.target_circle_id}`;
                return (
                  <li key={idx} className="p-2 rounded bg-slate-50 border border-slate-200">
                    <p className="text-xs font-semibold text-slate-800">
                      {op.action || 'edit'} - {roleName}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {op.data?.name || op.data?.purpose ? `${op.data?.name || ''} ${op.data?.purpose || ''}` : '...'}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <ProposalDiff
            original={tensionDraft.action === 'create' ? null : tensionDraft.original_data}
            proposed={tensionDraft.proposed_data}
            action={action}
            circleLabel={circleLabel}
          />
        )}

        {isAuthor && (
          <div className="border border-slate-200 rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-800">Votes</p>
              {tensionVotesLoading && <span className="text-xs text-slate-500">Actualisation...</span>}
            </div>
            {autoAcceptLabel && (
              <p className="text-xs text-slate-500 mb-2">
                Auto-accept le {autoAcceptLabel}
              </p>
            )}
            {tensionVotes.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun vote encore enregistré.</p>
            ) : (
              <div className="space-y-2">
                {tensionVotes.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
                  >
                    <span className="font-medium text-slate-800">{v.voter_name || v.voter_id}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        v.decision === 'accept'
                          ? 'bg-emerald-50 text-emerald-700'
                          : v.decision === 'reject'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {v.decision === 'pending' ? 'En attente' : v.decision === 'accept' ? 'Accepte' : 'Refuse'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <input
            value={tensionDraft.title || ''}
            onChange={(e) => onUpdateField('title', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold"
            readOnly={isLocked}
            placeholder="Titre de la tension"
          />
        </div>
        {showTopActions && tensionDraft.id && (
          <button
            onClick={() => onDelete(tensionDraft.id)}
            className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center text-sm"
          >
            <Trash2 size={14} className="mr-1" /> Supprimer
          </button>
        )}
        {showTopActions && tensionDraft.id && tensionDraft.status !== 'proposal' && tensionDraft.status !== 'applied' && (
          <button
            onClick={() => onPropose(tensionDraft.id)}
            className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 flex items-center text-sm"
            title="Lancer un vote"
          >
            Lancer un vote
          </button>
        )}
        {showTopActions && tensionDraft.id && tensionDraft.status === 'proposal' && isAuthor && (
          <button
            onClick={() => onWithdraw(tensionDraft.id)}
            className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 flex items-center text-sm"
            title="Retirer la proposition"
          >
            Retirer la proposition
          </button>
        )}
      </div>

      <textarea
        value={tensionDraft.summary || ''}
        onChange={(e) => onUpdateField('summary', e.target.value)}
        rows={2}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
        readOnly={isLocked}
        placeholder="Pourquoi cette tension ?"
      />

      <div className="flex flex-col gap-2">
        <label className="text-xs text-slate-500 font-semibold">Role qui exprime la tension</label>
        <select
          value={tensionDraft.expressed_by_role_id || ''}
          onChange={(e) => onUpdateField('expressed_by_role_id', e.target.value || null)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
          disabled={isLocked}
        >
          <option value="">Sélectionnez un role</option>
          {expressedRoles.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs text-slate-500 font-semibold">Cercle cible</label>
        <select
          value={tensionDraft.circle_id || ''}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : null;
            onUpdateField('circle_id', val);
            const selected = (circles || []).find((c) => String(c.id) === String(val));
            const snapshot = snapshotFromCircle(selected);
            onUpdateField('original_data', snapshot);
            onUpdateField('proposed_data', snapshot);
            updateOps(val ? [buildOperation(tensionDraft.action || 'edit', val)] : []);
          }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
          disabled={isLocked}
        >
          <option value="">Sélectionnez un cercle</option>
          {circleOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase text-slate-500 font-semibold">Actions proposees</p>
            <p className="text-[11px] text-slate-500">Une action suffit souvent. Ajoutez-en plusieurs seulement si elles doivent etre votees ensemble.</p>
          </div>
          <button
            type="button"
            onClick={() => addOperation('edit')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            disabled={isLocked}
          >
            <Plus size={14} /> Ajouter une action
          </button>
        </div>

        {localOps.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3">
            <p className="text-sm text-slate-600 mb-3">
            Aucune operation. Choisissez le cercle cible, puis ajoutez une action (créer/modifier/supprimer un role).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {ACTIONS.map((actionOption) => {
                const Icon = actionOption.icon;
                return (
                  <button
                    key={actionOption.value}
                    type="button"
                    onClick={() => addOperation(actionOption.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
                    disabled={isLocked || !tensionDraft.circle_id}
                  >
                    <span className="flex items-center gap-2 font-semibold text-slate-800">
                      <Icon size={15} />
                      {actionOption.label}
                    </span>
                    <span className="mt-1 block text-[11px] text-slate-500">{actionOption.detail}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {localOps.map((op, idx) => (
            <OperationCard
              key={idx}
              op={{ ...op, circle_id: tensionDraft.circle_id }}
              idx={idx}
              circles={circles}
              disabled={isLocked}
              onUpdate={(i, nextOp) => handleUpdateOp(i, { ...nextOp, circle_id: tensionDraft.circle_id })}
              onRemove={handleRemoveOp}
            />
          ))}
        </div>
      </div>

      {showFooterActions && (
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm">
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60"
            disabled={tensionsLoading}
          >
            {tensionDraft.id ? 'Mettre a jour' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TensionEditor;
